import React, { type FC } from 'react'
import { LinearProgress, Skeleton, Stack, type SxProps, TableCell, TableRow, type Theme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { Translation } from '../../../../domain'

import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { TABLE_CONTAINER_BORDER_WIDTH, TABLE_HEADER_ROW_HEIGHT } from '../../theme'
import { STICKY_HEADER_Z_INDEX } from './Header'
import { useColspan, usePageSize, useShouldShowHeader } from './State'

const MAX_SKELETON_ROWS = 10
const UNPAGED_SKELETON_ROWS = 5

const skeletonRowStyle: SxProps<Theme> = { gap: 3, alignItems: 'center' }
const skeletonMarkStyle: SxProps<Theme> = { flexShrink: 0, borderRadius: 0.5 }
const RELOADING_BAR_HEIGHT = 2

/** Above the sticky header */
const reloadingBarStyle: SxProps<Theme> = {
    position: 'absolute',
    left: TABLE_CONTAINER_BORDER_WIDTH,
    right: TABLE_CONTAINER_BORDER_WIDTH,
    height: RELOADING_BAR_HEIGHT,
    zIndex: STICKY_HEADER_Z_INDEX + 1,
}

/** Varies the bar lengths so the placeholder reads as text of different lengths, not as a grid */
const toBarWidth = (index: number): string => `${55 + ((index * 37) % 40)}%`

export const SkeletonTableBody: FC = () => {
    const colSpan = useColspan()
    const pageSize = usePageSize()
    const rows = Math.min(pageSize ?? UNPAGED_SKELETON_ROWS, MAX_SKELETON_ROWS)

    return (
        <>
            {Array.from({ length: rows }, (_, index) => (
                /** Only the first row stays exposed, to announce the loading once */
                <TableRow key={index} data-testid="table-skeleton-row" aria-hidden={index > 0 || undefined}>
                    <TableCell colSpan={colSpan}>
                        {index === 0 && (
                            <span style={visuallyHidden}>
                                <FormattedTranslation id={Translation.TABLE_LOADING} />
                            </span>
                        )}
                        <Stack direction="row" sx={skeletonRowStyle} aria-hidden>
                            <Skeleton variant="rectangular" animation="wave" width={20} height={20} sx={skeletonMarkStyle} />
                            <Skeleton variant="rounded" animation="wave" width={toBarWidth(index + 1)} height={12} />
                        </Stack>
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

/** Laid over the header's bottom edge instead of taking a row, so the rows below don't move when it comes and goes */
export const ReloadingBar: FC = () => {
    const translator = useTranslator()
    const top = TABLE_CONTAINER_BORDER_WIDTH + (useShouldShowHeader() ? TABLE_HEADER_ROW_HEIGHT - RELOADING_BAR_HEIGHT : 0)

    return (
        <LinearProgress data-testid="table-reloading" sx={[reloadingBarStyle, { top }]} aria-label={translator.formatTranslation(Translation.TABLE_LOADING)} />
    )
}
